import { Layers, Code, Sparkles, Cpu, Shield, Users, Square, Triangle, Circle, NotepadText, User } from 'lucide-react';
import { projects } from './ProjectData.js';
import RockGif from '../images/Rock-Scan.gif';

// Art imports
import AutumnHouse from '../images/Website Art/Autumn House.jpg';
import CharacterAutumn from '../images/Website Art/Character_Autumn.jpg';
import CharacterMushrooms from '../images/Website Art/Character_Mushrooms.jpg';
import CharacterSpring from '../images/Website Art/Character_Spring.jpg';
import CharacterWinter from '../images/Website Art/Character_Winter.jpg';
import ClimbingSwitzerland from '../images/Website Art/ClimbingSwitzerlandBrighter.jpg';
import ExploreOregon from '../images/Website Art/ExploreOregon.jpg';
import HouseOnHill from '../images/Website Art/HouseOnHill.jpg';
import HouseOnHill02 from '../images/Website Art/HouseOnHill_render.jpg';
import Patagonia from '../images/Website Art/Patagonia.jpg';
import PaulinaFalls from '../images/Website Art/PaulinaFalls.jpg';
import TempleColor from '../images/Website Art/TempleColor.jpg';
import Tolkien from '../images/Website Art/Tolkien.jpg';
import MR01 from '../images/MR_Selfie.jpg';
import MR02 from '../images/MR_Thief.jpg';
import Judy from '../images/Website Art/Judy.jpg';
import Rain from '../images/Website Art/Rain.jpg';
import Jesse from '../images/Website Art/JesseOwens.jpg';
import SpatialHeader from '../images/Image=Spatial.png';
import CreativeHeader from '../images/HeaderCreative.png';
import DeveloperHeader from '../images/Image=DevX.png'
import { image } from 'framer-motion/client';

// Scaniverse imports
import ScaniverseWeb from '../images/Scaniverse-webScreenshot.png';
import ScaniverseMobileAssets from '../images/scaniverse-mobileAssets.png';
import ScaniverseMobileSites from '../images/scaniverse-mobileSites.png';
import ScaniverseWebAssets from '../images/scaniverse-web-assets.png';
import ScaniverseWebScans from '../images/scaniverse-web-scans.png';

//scanning guidelines

import ScanningGuidance01 from '../images/Website Art/Scanning-technique.png';
import ScanningGuidance02 from '../images/Website Art/Scanning-technique-02.png';





export const pillars = [
    {
        // ============================================
        // BASIC INFO
        // ============================================
        id: 'spatial',
        title: 'Products & Features',
        icon: Layers,
        color: '#2d3234ff',
        cardImage: projects.find(p => p.id === 1)?.images[0],

        metadata: {
            role: 'UX & Product Design',
            timeline: '2021-2025',
            company: 'Niantic Spatial'
        },

        // ============================================
        // 1. HERO SECTION (appears first on page)
        // ============================================
        tagline: 'Designing and integrating new technology, from concept to launch.',
        overview: 'I design end-to-end product experiences for complex technical platforms, from core consumer products to enterprise tools. I focus on making sophisticated technology feel intuitive and shipping work that drives real impact.',

        // ============================================
        // 2. PROCESS CARDS (appears second on page)
        // ============================================
        process: [
            {
                // Card content (shows in process cards grid)
                title: 'Pokémon Go AR',
                description: 'I lead designs for new AR features, making their way into Pokémon Go’s new photo features. ',
                images: [
                    {
                        src: projects.find(p => p.id === 1)?.images[0],
                        caption: 'First VPS experience in Pokémon Go: Pokémon Playgrounds'
                    },
                    {
                        src: projects.find(p => p.id === 2)?.images[2],
                        caption: 'Scan data evaluating how we can author location-based AR content remotely'
                    }
                ],
                insights: [
                    'Identified necessary tools to support authoring location-based experiences, which are now available in Niantic Spatial SDK',
                    'Defined user tolerance thresholds for AR content stability and created tools to measure and evaluate them.',
                    'Safety measurements for location-based experiences, which allowed for a global launch of VPS in Pokémon Go'
                ],

                // ---- DETAIL SECTION (full page section that appears after challenge) ----
                detailSection: {
                    title: 'Pokémon Go AR', // Optional: override the card title
                    description: 'Bringing new technology into Pokémon Go', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Led design for new features',
                            text: 'I drove the UX and game design for the incorporation of new Niantic technology into Pokémon Go. My work established a framework for ensuring that players can engage with the features safely and informed the tools necessary to make this happen. Through thorough user testing, design iteration, and stakeholder alignment the features were released globally in Pokémon Go as GOSnapshot and Pokémon Playgrounds.'
                        },
                        {
                            subheader: 'Advanced tools and technology',
                            text: 'I designed the first prototypes ever testing our new visual positioning system with Pokémon Go players, which shaped the roadmap for Niantic technology and developer tools. The prototype was used to measure the accuracy of the new AR technology and my workflow informed tools necessary for engaging with Niantic’s SDK.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                            src: projects.find(p => p.id === 1)?.images[1],
                            caption: 'First VPS experience in Pokémon Go: Pokémon Playgrounds',
                        },
                        {
                            src: projects.find(p => p.id === 1)?.images[2],
                            caption: 'Scan data I created to author location-based AR content remotely. This established a new workflow that was integrated into the tools.',
                        },
                        {
                            src: projects.find(p => p.id === 1)?.images[3],
                            caption: 'I led designs for how we can integrate our new technology into Pokémon Go.',
                        },
                        {
                            src: projects.find(p => p.id === 1)?.images[4],
                            //caption: '',
                        }
                    ],

                    // Stats/metrics (displays at bottom)
                    metrics: [
                        {
                            label: 'Global Release',
                            description: 'Shipped to players worldwide. '
                        },
                        {
                            label: '10+ Million Users',
                            description: 'See how people are using the feature with #GoSnapshot'
                        },
                        {
                            label: 'First VPS in Games',
                            description: 'First globally used VPS feature in AR games.'
                        }
                    ]
                }
            },
            {
                title: 'Scaniverse for Enterprise',
                description: 'Co-led the design for Niantic Spatial\'s core scanning product across mobile and desktop.',
                images: [
                    {
                        src: ScaniverseWeb,
                        caption: '[Add caption]',
                    },
                ],
                insights: [
                    '[Add insight 1]',
                    '[Add insight 2]',
                    '[Add insight 3]'
                ],

                // ---- DETAIL SECTION (full page section that appears after challenge) ----
                detailSection: {
                    title: 'Scaniverse for Enterprise',
                    description: 'Design for Niantic Spatial\'s core scanning and data management tool for enterprise customers.',

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Design Across Mobile and Web',
                            text: 'Co-led the design for Niantic Spatial\'s core scanning product, creating a cohesive experience that works seamlessly across mobile and web platforms. This required understanding the unique constraints and opportunities of each platform while maintaining consistent workflows and visual language that serves enterprise customers.'
                        },
                        {
                            subheader: 'Simplifying Complex Workflows',
                            text: 'In partnership with another designer, I focused on translating complex scanning processes and data management systems into intuitive flows that make sophisticated spatial technology accessible. By breaking down technical operations into clear, manageable steps, I enabled users to capture, process, and manage spatial data without needing to understand the underlying complexity.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        
                        {
                            src: ScaniverseWeb,
                            caption: 'Splat viewer on Scaniverse web'
                        },
                        {
                            src: ScaniverseWebAssets,
                            caption: 'Asset management'
                        },
                        {
                            src: ScaniverseWebScans,
                            caption: 'Scan management'
                        }
                    ],

                    // Additional image sections
                    additionalImageSections: [
                        {
                            images: [
                                {
                                    src: ScaniverseMobileAssets,
                                    caption: 'Scaniverse on mobile'
                                },
                                {
                                    src: ScaniverseMobileSites,
                                    caption: 'Mobile site management'
                                }
                            ]
                        }
                    ],

                    // Stats/metrics (optional - uncomment and fill in if needed)
                    // metrics: [
                    //     {
                    //         label: '[Add metric label]',
                    //         description: '[Add metric description]'
                    //     },
                    //     {
                    //         label: '[Add metric label]',
                    //         description: '[Add metric description]'
                    //     }
                    // ]
                }
            },
            {
                title: 'Evaluate technology and deliver custom solutions',
                description: 'I evaluate new technology and customer needs to establish product vision, and design and deliver custom solutions to enterprise customers.',
                images: [
                    {
                        src: projects.find(p => p.id === 2)?.images[0],
                        caption: 'Long-distance localization prototype for AR positioning'
                    },
                    {
                        src: projects.find(p => p.id === 6)?.images[0],
                        caption: 'Custom enterprise solution'
                    }
                ],
                insights: [
                    'Designed and built prototypes validating the readiness of new technology for customer use-cases',
                    'Led designs integrating our technology into enterprise customer products across entertainment, e-commerce, and government',
                    'Collaborated with Product, Engineering, and Research teams to strategically evolve our technology based on customer needs'
                ],

                // ---- DETAIL SECTION (full page section that appears after challenge) ----
                detailSection: {
                    title: 'Custom Solutions',
                    description: 'Bridging new technology capabilities with customer needs to deliver real-world solutions',

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Evaluate Technology and Customer Needs',
                            text: 'I design and build early prototypes that reveal how new AR and spatial technologies behave in real conditions while meeting directly with customers to understand their goals, workflows, and technical constraints. Through rapid iteration and discovery, I identify where our technology can create meaningful value, validate performance across mobile, headset, and desktop environments, and establish product vision grounded in both technical capabilities and real customer needs.'
                        },
                        {
                            subheader: 'Design and Deliver Custom Solutions',
                            text: 'I translate technical capabilities into practical solutions for enterprise customers, designing end-to-end prototypes and experience designs tailored to their use-cases. I design and present pitch decks that communicate both product vision and technical feasibility, supporting business development across customers in entertainment, e-commerce, and government. I also produce marketing assets and visuals that support the BD team in driving adoption and closing partnerships.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                            src: projects.find(p => p.id === 2)?.images[0],
                            caption: 'I lead the design for our first test using long-distance localization, allowing content to be accurately positioned from a distance.'
                        },
                        {
                            src: projects.find(p => p.id === 6)?.images[0],
                            caption: 'Custom enterprise solution'
                        },
                        {
                            src: projects.find(p => p.id === 2)?.images[2],
                            caption: 'Wayfinding project integrating wearables with AR.'
                        },
                        {
                            src: projects.find(p => p.id === 6)?.images[1],
                            caption: 'Remote collaboration tools for spatial development.'
                        },
                        {
                            src: projects.find(p => p.id === 6)?.images[2],
                            caption: 'Developing a feature to span across mobile, headset, and desktop devices'
                        }
                        
                    ],
                    additionalImageSections: [
                        {
                            images: [
                                {
                                    src: projects.find(p => p.id === 2)?.images[1],
                                    caption: 'Developer sample demonstrating new multiplayer technology.'
                                },
                            ]
                        }
                    ]

                }
            },

        ],

        // ============================================
        // 3. CHALLENGE SECTION (appears after process cards)
        // ============================================
        problem: {
            title: 'Translate new technology into real products.',
            points: [
                {
                    icon: Users,
                    title: 'Collaboration',
                    description: 'Unproved tech requires close collaboration with engineering and research to understand its capabilities and design UX patterns that bridge gaps in the system.'
                },
                {
                    icon: NotepadText,
                    title: 'Validation',
                    description: 'New technology and use-cases requires rigorous testing to determine the readiness both the technology and product.'
                },
                {
                    icon: User,
                    title: 'People First',
                    description: 'Building products that people want to use and can stay safe while doing so.'
                }
            ],
        },
        

    },
    {
        id: 'developer-tools',
        title: 'Developer Tools',
        icon: Code,
        tagline: 'Building tools that empower creatives.',
        color: '#2d3234ff',
        //heroImage: RockGif, // Placeholder - change to your preferred image
        cardImage: DeveloperHeader, // Image for home page card - you can change this to any imported image

        metadata: {
            role: 'UX & Product Design',
            timeline: '2021-2025',
            team: 'Developer Experience Team'
        },

        // Section labels (optional - will use defaults if not specified)
        sectionLabels: {
            overview: 'Introduction',
            process: 'Process',
            outcomes: 'Impact',
            learnings: 'Learnings'
        },

        overview: 'As a UX and product designer actively prototyping with our SDK, I experienced the same workflow challenges our external developers faced. I used those insights to redesign the tools, interfaces, and guidance that support spatial development: integrated improved VPS workflows into Niantic Studio, build our mapping and coverage tools, enhanced our scanning UX, and restructuring our developer samples and documentation. These changes helped make the technology more discoverable, reliable, and easier to adopt.',

        // Add your developer tools case study content here
        process: [
            {
                title: 'Developer Experience',
                description: 'Design and support for developer documentation and samples',
                images: [
                    {
                        src: projects.find(p => p.id === 5)?.images[6],
                        caption: 'Integration of location-based AR tools into Niantic Spatial'
                    }
                ],
                detailSection: {
                    // heroImage: projects.find(p => p.id === 2)?.images[1], // Optional: large image at top
                    title: 'Developer Experience', // Optional: override the card title
                    description: 'Documentation, Samples, and experience for developers using the SDK.', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Developer Documentation',
                            text: 'I created documentation that supports developers working across multiple platforms and coding languages. My focus was on clarity, navigation, and demonstrating best practices, ensuring that developers could quickly understand how to integrate and use our SDK. This included restructuring content, improving information hierarchy, and aligning terminology across teams.'
                        },
                        {
                            subheader: 'Improving samples and aligning with company brand',
                            text: 'I redesigned our engineering samples to better showcase how the technology works in real scenarios, making them easier to understand and adopt. I also aligned our SDK documentation and language with broader company goals and branding, creating a cohesive developer experience that reflects our product vision and technical capabilities.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                            src: projects.find(p => p.id === 5)?.images[2],
                            caption: ''
                        },
                        {
                            src: projects.find(p => p.id === 5)?.images[4],
                            caption: ''
                        },
                        {
                            src: projects.find(p => p.id === 5)?.images[5],
                            caption: '',
                        }
                    ],

                    additionalImageSections: [
                        {
                            title: 'Scanning Guidelines',
                            images: [
                                {
                                    src: ScanningGuidance01,
                                    caption: 'Scanning guidelines for Scaniverse'
                                },
                                {
                                    src: ScanningGuidance02,
                                    caption: 'Scanning guidelines for Scaniverse'
                                }
                            ]
                        }
                    ]
                }
            },
            {
                title: 'Developer Tools',
                description: 'Integration of new technology and workflows into developer tools.',
                images: [
                    {
                        src: projects.find(p => p.id === 5)?.images[1],
                        caption: 'Integration of location-based AR tools into Niantic Spatial'
                    }
                ],
                detailSection: {
                    // heroImage: projects.find(p => p.id === 2)?.images[1], // Optional: large image at top
                    title: 'Developer Tools', // Optional: override the card title
                    description: 'Integration of new technology and workflows into developer tools.', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Improve Developer Workflow',
                            text: 'Drawing on my own development experience and research with external developers, I refined workflows across our tooling ecosystem. I identified friction points, clarified mental models, and guided improvements that helped developers work more efficiently and confidently within our platform.'
                        },
                        {
                            subheader: 'Designing Features and Prototypes for Better Tooling',
                            text: 'I integrated new capabilities into our visual development platform and designed UX prototypes that demonstrated how these features should function end to end. These prototypes informed engineering decisions, provided actionable guidelines, and ensured that new tooling aligned with developer needs and product goals.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                            src: projects.find(p => p.id === 5)?.images[0],
                            caption: ''
                        },
                        {
                            src: projects.find(p => p.id === 5)?.images[1],
                            caption: ''
                        }
                    ],
                }
            }
        ]
        
    },
    {
        id: 'creative',
        title: 'Creative Experiences',
        icon: Sparkles,
        tagline: 'Bringing art, play, and education together into interactive experiences.',
        color: '#2d3234ff',
        //heroImage: RockGif, // Placeholder - change to your preferred image
        cardImage: CreativeHeader, // Image for home page card - you can change this to any imported image

        // Section labels (optional - will use defaults if not specified)
        sectionLabels: {
            overview: 'Introduction',
            process: 'Games',
            outcomes: 'Illustration',
            learnings: 'Learnings'
        },


        // Add your creative experiences case study content here
        process: [
            {
                title: 'Flora & Fungi',
                description: 'A game exploring the symbiotic relationship between mycelium networks and forest ecosystems.',
                metadata: {
                    role: 'Game Designer & Developer',
                    timeline: '2020-2021',
                    team: 'Solo MA thesis Project',
                    tools: 'Unity, C#, Blender, Shaders'

                },
                images: [
                    {
                        src: projects.find(p => p.id === 7)?.images[0],
                        caption: 'Gameplay showing the mycelium network spreading through the forest'
                    },
                    {
                        src: projects.find(p => p.id === 7)?.images[1],
                        caption: 'Forest ecosystem responding to player choices'
                    },
                    {
                        src: projects.find(p => p.id === 7)?.images[2],
                        caption: 'Master\'s thesis exploring games and environmental understanding'
                    }
                ],
                 detailSection: {
                    // heroImage: projects.find(p => p.id === 2)?.images[1], // Optional: large image at top
                    title: 'Flora & Fungi', // Optional: override the card title
                    description: 'Exploring how we can become more empathetic to nature through games.', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Game Overview',
                            text: 'I designed and developed a solo game exploring the symbiotic relationship between mycelium networks and forest ecosystems. Players control the mycelium, influencing whether the forest flourishes or collapses.'
                        },
                        {
                            subheader: 'Master Thesis Project',
                            text: 'The project was created as part of my master\'s thesis, which examined how games can deepen our understanding and behavior with the environment.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                        src: projects.find(p => p.id === 7)?.images[0],
                        caption: 'Gameplay showing the mycelium network spreading through the forest'
                    },
                    {
                        src: projects.find(p => p.id === 7)?.images[1],
                        caption: 'Forest ecosystem responding to player choices'
                    },
                    {
                        src: projects.find(p => p.id === 7)?.images[2],
                        caption: 'Master\'s thesis exploring games and environmental understanding'
                    }
                    ],          
                }
            },
            {
                title: 'Breath Beneath',
                description: "An immersive and interactive lightshow.",
                metadata: {
                    role: 'Game Designer & Developer',
                    timeline: '2021',
                    team: '2',
                    tools: 'Unity, C#, Blender, Kinect'

                },
                images: [
                    {
                        src: projects.find(p => p.id === 8)?.images[0],
                        caption: 'The installation lights up in response to participants\' breathing'
                    },
                    {
                        src: projects.find(p => p.id === 8)?.images[1],
                        caption: 'A responsive particle system was created to form around natural forms'
                    },
                    {
                        src: projects.find(p => p.id === 8)?.images[2],
                        caption: 'Concept art of the space'
                    },
                     {
                        src: projects.find(p => p.id === 8)?.images[3],
                        caption: '3D scan of the bunker connected to a hand-modeled garden to match the real space'
                    }
                ],
                detailSection: {
                    // heroImage: projects.find(p => p.id === 2)?.images[1], // Optional: large image at top
                    title: 'Breath Beneath', // Optional: override the card title
                    description: 'An immersive and interactive lightshow.', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'A Design Exhibition',
                            text: 'Breath Beneath was an immersive installation created for the Zürich Design Biennale. Visitors entered an underground bunker in the Zürich Botanical Gardens, where the ceiling above them illuminated with a living network of roots that responded to their breath.',
                        },
                        {
                            subheader: 'The Experience',
                            text: 'Using a Kinect sensor, silhouettes of people walking above were projected through the roots, creating a poetic connection between the seen and unseen layers of life. Together with the other participants, visitors would match the rhythm of their breathing to light up the ceiling above them. The experience was described by guests as “transcendent.”'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                        src: projects.find(p => p.id === 8)?.images[1],
                        caption: 'The installation lights up in response to participants\' breathing'
                    },
                    {
                        src: projects.find(p => p.id === 8)?.images[0],
                        caption: 'A responsive particle system was created to form around natural forms'
                    },
                    {
                        src: projects.find(p => p.id === 8)?.images[2],
                        caption: 'Concept art of the space'
                    },
                     {
                        src: projects.find(p => p.id === 8)?.images[3],
                        caption: '3D scan of the bunker connected to a hand-modeled garden to match the real space'
                    }
                    ],          
                }
            },
            {
                title: 'Ghost Host',
                description: "An educational game for kids",
                metadata: {
                    role: 'Game Designer & Developer',
                    timeline: '2019',
                    team: '2',
                    tools: 'Unity, C#, Spline'
                },
                images: [
                    {
                        src: projects.find(p => p.id === 9)?.images[0],
                        caption: 'The installation lights up in response to participants\' breathing'
                    },
                    {
                        src: projects.find(p => p.id === 9)?.images[1],
                        caption: 'A responsive particle system was created to form around natural forms'
                    }
                ],
                detailSection: {
                    // heroImage: projects.find(p => p.id === 2)?.images[1], // Optional: large image at top
                    title: 'Ghost Host', // Optional: override the card title
                    description: 'An educational game for kids.', // Appears below title

                    // Two-column text sections
                    textSections: [
                        {
                            subheader: 'Project Purpose',
                            text: 'Ghost Host is an educational game for kids that was cocreated with Zürich University of Applied Sciences. The game is designed to educate children in Switzerland on the land-usages of agriculture.'
                        },
                        {
                            subheader: 'Game Overview',
                            text: 'The player must farm crops and raise livestock to feed as an offering to the ghosts who visit the shrine. If they fail to feed the ghosts, the ghosts will attack the shrine and the player will lose their farm. The player must strategize how to fit all the crops they can in their farm space, with each tile representing the amount of land needed by that food.'
                        }
                    ],

                    // Image carousel (displays below text sections)
                    images: [
                        {
                        src: projects.find(p => p.id === 9)?.images[0],
                        caption: 'The installation lights up in response to participants\' breathing'
                    },
                    {
                        src: projects.find(p => p.id === 9)?.images[1],
                        caption: 'A responsive particle system was created to form around natural forms'
                    }
                    ],          
                }
            },
            
        ],

        // Outcomes section for illustration gallery
        outcomes: {
            description: 'A collection of my illustrations and digital art exploring themes of nature, adventure, and character design. These pieces span from personal projects to commissioned work, showcasing my love for storytelling through visual art.',
            metadata: {
                role: 'Illustrator & Designer',
                timeline: '2018-Present'
            },
            layout: 'gallery', // Special property to indicate gallery layout
            images: [
                {
                    src: AutumnHouse,
                    caption: 'Autumn House'
                },
                {
                    src: ClimbingSwitzerland,
                    caption: 'Climbing Switzerland'
                },
                {
                    src: ExploreOregon,
                    caption: 'Explore Oregon'
                },
                {
                    src: HouseOnHill,
                    caption: 'House on a Hill'
                },
                {
                    src: HouseOnHill02,
                    caption: 'House on a Hill Process'
                },
                {
                    src: MR01,
                    caption: 'Museum Rietberg Activity Card'
                },
                {
                    src: MR02,
                    caption: 'Museum Rietberg Activity Card'
                },
                {
                    src: Patagonia,
                    caption: 'Patagonia'
                },
                {
                    src: CharacterSpring,
                    caption: 'Character Design - Spring'
                },
                {
                    src: CharacterMushrooms,
                    caption: 'Character Design - Summer'
                },
                {
                    src: CharacterAutumn,
                    caption: 'Character Design - Autumn'
                },
                {
                    src: CharacterWinter,
                    caption: 'Character Design - Winter'
                },
                {
                    src: PaulinaFalls,
                    caption: 'Paulina Falls'
                },
                {
                    src: TempleColor,
                    caption: 'Temple'
                },
                {
                    src: Tolkien,
                    caption: 'Tolkien-Inspired Illustration'
                },
                {
                    src: Judy,
                    caption: 'Judy Collins Illustration'
                },
                {
                    src: Rain,
                    caption: 'The Rain'
                },
                {
                    src: Jesse,
                    caption: 'Jesse Owens Illustration'
                }
            ]
        }

    }
];

// ── Art page exports ──────────────────────────────────────────────────────────
// Illustration gallery pulled from the creative pillar outcomes
export const artGallery = pillars.find(p => p.id === 'creative')?.outcomes?.images ?? [];

// Game / interactive project IDs shown on the Art page
export const creativeProjectIds = [7, 8, 9];
